import { css } from '@emotion/css';
import { useNavigate } from 'react-router-dom';
import { Award, Calendar, Users, Mail } from 'lucide-react';

const page = css({
  width: '100%',
  maxWidth: '1200px',
  margin: '0 auto',
  padding: 'var(--spacing-2xl) var(--spacing-lg)',
  backgroundColor: 'var(--primary-color)',
  minHeight: 'calc(100vh - 200px)',
  '@media (max-width: 768px)': {
    padding: 'var(--spacing-xl) var(--spacing-md)',
  },
  '@media (max-width: 480px)': {
    padding: 'var(--spacing-lg) var(--spacing-sm)',
  },
});

const container = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--spacing-2xl)',
});

const header = css({
  textAlign: 'center',
  marginBottom: 'var(--spacing-xl)',
  paddingBottom: 'var(--spacing-xl)',
  borderBottom: '2px solid var(--pc-200)',
});

const title = css({
  fontSize: 'var(--fs-900)',
  fontWeight: 700,
  color: 'var(--pc-700)',
  marginBottom: 'var(--spacing-md)',
  '@media (max-width: 768px)': {
    fontSize: 'var(--fs-800)',
  },
  '@media (max-width: 480px)': {
    fontSize: 'var(--fs-700)',
  },
});

const subtitle = css({
  fontSize: 'var(--fs-500)',
  color: 'var(--g-700)',
  fontWeight: 400,
  lineHeight: 1.6,
  maxWidth: '800px',
  margin: '0 auto',
  '@media (max-width: 768px)': {
    fontSize: 'var(--fs-450)',
  },
  '@media (max-width: 480px)': {
    fontSize: 'var(--fs-400)',
  },
});

const cardsGrid = css({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
  gap: 'var(--spacing-xl)',
  marginTop: 'var(--spacing-xl)',
  '@media (max-width: 768px)': {
    gridTemplateColumns: '1fr',
  },
});

const card = css({
  padding: 'var(--spacing-2xl)',
  backgroundColor: 'rgba(var(--pcrgba-50), 0.5)',
  borderRadius: 'var(--spacing-md)',
  border: '2px solid var(--pc-200)',
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--spacing-md)',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
    borderColor: 'var(--pc-400)',
  },
});

const cardIcon = css({
  color: 'var(--pc-600)',
  marginBottom: 'var(--spacing-sm)',
});

const cardTitle = css({
  fontSize: 'var(--fs-600)',
  fontWeight: 600,
  color: 'var(--pc-700)',
  marginBottom: 'var(--spacing-xs)',
});

const cardDescription = css({
  fontSize: 'var(--fs-400)',
  color: 'var(--g-700)',
  lineHeight: 1.6,
  marginBottom: 'var(--spacing-md)',
});

const cardAction = css({
  fontSize: 'var(--fs-400)',
  fontWeight: 600,
  color: 'var(--pc-600)',
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--spacing-2xs)',
  marginTop: 'auto',
  '&::after': {
    content: '"→"',
    transition: 'transform 0.3s ease',
  },
  [`${card}:hover &::after`]: {
    transform: 'translateX(4px)',
  },
});

const ctaSection = css({
  marginTop: 'var(--spacing-2xl)',
  padding: 'var(--spacing-2xl)',
  backgroundColor: 'rgba(var(--pcrgba-100), 0.3)',
  borderRadius: 'var(--spacing-md)',
  textAlign: 'center',
});

const ctaTitle = css({
  fontSize: 'var(--fs-600)',
  fontWeight: 600,
  color: 'var(--pc-700)',
  marginBottom: 'var(--spacing-md)',
});

const ctaText = css({
  fontSize: 'var(--fs-400)',
  color: 'var(--g-700)',
  lineHeight: 1.6,
  marginBottom: 'var(--spacing-lg)',
});

const ctaButton = css({
  padding: 'var(--spacing-md) var(--spacing-xl)',
  backgroundColor: 'var(--pc-600)',
  color: 'var(--primary-color)',
  border: 'none',
  borderRadius: 'var(--spacing-sm)',
  fontSize: 'var(--fs-400)',
  fontWeight: 600,
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: 'var(--pc-700)',
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  },
});

export function CommercialIndex() {
  const navigate = useNavigate();

  const handleNavigate = (path: string) => {
    navigate(path);
  };

  return (
    <div className={page}>
      <div className={container}>
        <div className={header}>
          <h1 className={title}>Soluções Comerciais</h1>
          <p className={subtitle}>
            Descubra como a Anchor Ingressos pode ajudar seu negócio a crescer. Oferecemos
            soluções completas para patrocinadores, produtores de eventos e vendas em grupo.
          </p>
        </div>

        <div className={cardsGrid}>
          <div className={card} onClick={() => handleNavigate('/comercial/patrocinador')}>
            <Award className={cardIcon} size={48} />
            <h2 className={cardTitle}>Seja um Patrocinador</h2>
            <p className={cardDescription}>
              Associe sua marca a eventos exclusivos e alcance um público engajado. Oferecemos
              diversos níveis de patrocínio com máxima visibilidade e retorno garantido.
            </p>
            <span className={cardAction}>Conhecer oportunidades</span>
          </div>

          <div className={card} onClick={() => handleNavigate('/comercial/vender-evento')}>
            <Calendar className={cardIcon} size={48} />
            <h2 className={cardTitle}>Venda Seu Evento</h2>
            <p className={cardDescription}>
              Plataforma completa para produtores de eventos. Gestão simplificada, pagamentos
              seguros, análise em tempo real e suporte especializado.
            </p>
            <span className={cardAction}>Começar a vender</span>
          </div>

          <div className={card} onClick={() => handleNavigate('/comercial/vendas-grupos')}>
            <Users className={cardIcon} size={48} />
            <h2 className={cardTitle}>Vendas para Grupos</h2>
            <p className={cardDescription}>
              Condições especiais para empresas, escolas e grupos. Descontos progressivos,
              benefícios exclusivos e atendimento prioritário.
            </p>
            <span className={cardAction}>Solicitar proposta</span>
          </div>
        </div>

        <div className={ctaSection}>
          <Mail className={cardIcon} size={40} style={{ display: 'inline-block' }} />
          <h2 className={ctaTitle}>Ainda tem dúvidas?</h2>
          <p className={ctaText}>
            Nossa equipe está pronta para apresentar a melhor solução para o seu negócio.
            Entre em contato e descubra como podemos ajudar.
          </p>
          <button className={ctaButton} onClick={() => navigate('/contato')}>
            Entrar em Contato
          </button>
        </div>
      </div>
    </div>
  );
}

export default CommercialIndex;
